import React, { useMemo } from 'react';
import CalendarCell from './CalendarCell';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarGrid = ({ year, month, events, batches = [], interviewEvents = [], workingDays, onDateClick, onEventClick }) => {
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  // Build grid — use 5 rows when possible, 6 only if needed
  const gridDates = useMemo(() => {
    const first = new Date(year, month, 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const totalCells = startDow + daysInMonth > 35 ? 42 : 35;
    const start = new Date(year, month, 1 - startDow);
    const dates = [];
    for (let i = 0; i < totalCells; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, [year, month]);

  // Index events by date for O(1)
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const evt of events) {
      const s = new Date(evt.startDate); s.setHours(0, 0, 0, 0);
      const e = new Date(evt.endDate); e.setHours(0, 0, 0, 0);
      const c = new Date(s);
      while (c <= e) {
        const k = c.toDateString();
        if (!map[k]) map[k] = [];
        map[k].push(evt);
        c.setDate(c.getDate() + 1);
      }
    }
    return map;
  }, [events]);

  // Index batches by date for O(1)
  const batchesByDate = useMemo(() => {
    const map = {};
    for (const batch of batches) {
      // Determine working days based on batch type
      const batchWorkingDays = batch.batchType === "WEEKEND" ? [0, 6] : workingDays;
      
      // Calculate session dates from startDate
      if (batch.startDate) {
        const start = new Date(batch.startDate);
        start.setHours(0, 0, 0, 0);
        
        // Create sessions for 60 days from start (can adjust this range)
        const c = new Date(start);
        const maxDate = new Date(start);
        maxDate.setDate(maxDate.getDate() + 60);
        
        while (c <= maxDate) {
          if (batchWorkingDays.includes(c.getDay())) {
            const k = c.toDateString();
            if (!map[k]) map[k] = [];
            map[k].push(batch);
          }
          c.setDate(c.getDate() + 1);
        }
      }
    }
    return map;
  }, [batches, workingDays]);

  // Index interview events by date for O(1)
  const interviewsByDate = useMemo(() => {
    const map = {};
    for (const evt of interviewEvents) {
      if (!evt.date) continue;
      const d = new Date(evt.date);
      d.setHours(0, 0, 0, 0);
      const k = d.toDateString();
      if (!map[k]) map[k] = [];
      map[k].push(evt);
    }
    return map;
  }, [interviewEvents]);

  const isNonWorking = (date) => {
    const evts = eventsByDate[date.toDateString()] || [];
    for (const e of evts) {
      if (e.isWorkingDay === false) return true;
      if (e.isWorkingDay === true) return false;
    }
    return !workingDays.includes(date.getDay());
  };

  return (
    <div className="ic-grid">
      {DOW.map((d, i) => (
        <div key={d} className={`ic-dow ${!workingDays.includes(i) ? 'ic-dow-off' : ''}`}>
          {d}
        </div>
      ))}
      {gridDates.map((date, idx) => (
        <CalendarCell
          key={idx}
          date={date}
          isCurrentMonth={date.getMonth() === month && date.getFullYear() === year}
          isToday={date.toDateString() === today.toDateString()}
          isNonWorking={isNonWorking(date)}
          events={date.getMonth() === month ? (eventsByDate[date.toDateString()] || []) : []}
          batches={date.getMonth() === month ? (batchesByDate[date.toDateString()] || []) : []}
          interviewEvents={date.getMonth() === month ? (interviewsByDate[date.toDateString()] || []) : []}
          onDateClick={onDateClick}
          onEventClick={onEventClick}
        />
      ))}
    </div>
  );
};

export default CalendarGrid;
